import {
  AutoComplete,
  Button,
  Input,
  message,
  Select,
  SelectProps,
  UploadFile,
} from "antd";
import { ReactNode, useEffect, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import AttachmentIcon from "../../../assets/icons/AttachmentIcon";
import CloseIcon from "../../../assets/icons/CloseIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import DraftIcon from "../../../assets/icons/DraftIcon";
import InboxIcon from "../../../assets/icons/InboxIcon";
import SentIcon2 from "../../../assets/icons/SentIcon2";
import StarredIcon from "../../../assets/icons/StarredIcon";
import XIcon from "../../../assets/icons/XIcon";
import DeleteConfirmationModal from "../../../components/common/Communication/DeleteConfirmationModal";
import { mailRegex } from "../../../constants/constants";
import { useFileUploadMutation } from "../../../features/superAdmin/employeeService/employeeManagementService";
import {
  useAddSaveDraftMailMutation,
  useDeleteMailMutation,
  useGetSingleMailByIdQuery,
  useLazyGetUserByEmailQuery,
  useSendMailMutation,
  useUpdateDraftMailMutation,
} from "../../../features/superAdmin/ticketService/ticketServiceApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";

export default function Email({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<SelectProps["options"]>([]);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const { open, handleOpen, handleClose } = useModal();
  const [sendMail, { isLoading, isSuccess }] = useSendMailMutation();
  const [
    addSaveDraftMail,
    {
      isSuccess: isSaveDraftSuccess,
      isError: isSaveDradtError,
      error: saveDradtError,
      isLoading: isSaveDradtLoading,
    },
  ] = useAddSaveDraftMailMutation();
  const [
    updateDraft,
    {
      isSuccess: isUpdateDraftSuccess,
      isError: isUpdateDraftError,
      error: updateDraftError,
      isLoading: isUpdateDraftLoading,
    },
  ] = useUpdateDraftMailMutation();
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [triggerGetUser] = useLazyGetUserByEmailQuery();
  const auth = useAppSelector((state) => state.auth);
  const [activeCc, setActiveCc] = useState(false);
  const [activeBcc, setActiveBcc] = useState(false);
  const [ccValues, setCcvalues] = useState<string[]>([]);
  const [bccValues, setBccValues] = useState<string[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [fileUplaod, { data: fileUploadData }] = useFileUploadMutation();
  const [
    deleteMail,
    {
      isSuccess: isDeleteMailSuccess,
      isError: isDeleteMailError,
      error: deleteMailError,
      isLoading: isDeleteMailLoading,
    },
  ] = useDeleteMailMutation();

  const [searchParams] = useSearchParams();
  const mail_id = searchParams.get("mail_id");
  const navigate = useNavigate();
  const location = useLocation();
  const isNumeric = (value: string | null) =>
    value !== null && /^\d+$/.test(value);

  const {
    data: singleEmailData,
    isError: isSingleEmailError,
    error: singleEmailError,
    isLoading: isSingleEmailLoading,
  } = useGetSingleMailByIdQuery(mail_id, { skip: !mail_id || !auth?.user });
  const [
    fileUpload,
    {
      error: fileUploadError,
      isError: fileUploadIsError,
      isLoading: isFileUploading,
    },
  ] = useFileUploadMutation();
  useError(fileUploadIsError, fileUploadError, "Upload photo fail");
  const [attachments, setAttachments] = useState<string[]>([]);

  useError(isSaveDradtError, saveDradtError, "Mail save draft fail");
  useError(isUpdateDraftError, updateDraftError, "Mail save draft fail");
  useError(isSingleEmailError, singleEmailError, "Single mail data fail");

  useEffect(() => {
    setAttachments([]);
  }, [showComposeModal]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const maxFileSize = 30 * 1024 * 1024; // 30MB

    for (const file of files) {
      if (file.size > maxFileSize) {
        toast("error", `"${file.name}" exceeds max file size of 30MB.`);
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);

      try {
        const res: any = await fileUpload(formData).unwrap();
        if (res?.Location) {
          setAttachments((prev) => [...prev, res.Location]); // Add to list
        }
      } catch (err) {
        console.error(`Upload failed for ${file.name}`, err);
      }
    }

    e.target.value = ""; // Reset input
  };

  const handleSendMail = async () => {
    if (!recipientEmail) return;

    const { data } = await triggerGetUser(recipientEmail);
    if (!data) {
      console.error("User not found.");
      return;
    }

    const ccIdList = await Promise.all(
      ccValues.map(async (email) => {
        const trimmedEmail = email.trim();
        const { data: ccData } = await triggerGetUser(trimmedEmail);
        return ccData?.data[0]?.id;
      }),
    );

    const bccIdList = await Promise.all(
      bccValues.map(async (email) => {
        const trimmedEmail = email.trim();
        const { data: bccData } = await triggerGetUser(trimmedEmail);
        return bccData?.data[0]?.id;
      }),
    );

    const attachmentUrls = fileList
      .map((file) => {
        const url = file.url || file.response?.url || "";
        return url.toString();
      })
      .filter((url): url is string => typeof url === "string" && url !== "");

    // console.log("Attachment URLs:", attachmentUrls);

    const payload = {
      senderId: auth.user?.id,
      receiverId: data.data[0]?.id,
      ccUserIds: ccIdList || [],
      bccUserIds: bccIdList || [],
      subject: subject,
      body: emailBody,
      attachments: attachments,
      isDraft: false,
    };

    // console.log("Payload for sending mail:", payload);

    sendMail(payload);
  };

  const handleDraftMail = async () => {
    if (!recipientEmail) return;

    const { data } = await triggerGetUser(recipientEmail);
    if (!data) {
      console.error("User not found.");
      return;
    }

    const ccIdList = await Promise.all(
      ccValues.map(async (email) => {
        const trimmedEmail = email.trim();
        const { data: ccData } = await triggerGetUser(trimmedEmail);
        return ccData?.data[0]?.id;
      }),
    );

    const bccIdList = await Promise.all(
      bccValues.map(async (email) => {
        const trimmedEmail = email.trim();
        const { data: bccData } = await triggerGetUser(trimmedEmail);
        return bccData?.data[0]?.id;
      }),
    );

    const attachmentUrls = fileList
      .map((file) => {
        const url = file.url || file.response?.url || "";
        return url.toString();
      })
      .filter((url): url is string => typeof url === "string" && url !== "");

    const payload = {
      receiverId: data.data[0]?.id,
      subject: subject,
      body: emailBody,
      ccUserIds: ccIdList || [],
      bccUserIds: bccIdList || [],
      // attachments: attachmentUrls,
    };
    if (singleEmailData?.data?.id) {
      updateDraft({ id: singleEmailData?.data?.id, data: payload });
    } else {
      addSaveDraftMail({ id: auth.user?.id, data: payload });
    }
  };

  const handleDeleteMail = async () => {
    deleteMail({
      id: mail_id,
      data: {
        userId: auth.user?.id,
      },
    });
  };

  const handleSearch = async (text: string) => {
    const { data } = await triggerGetUser(text);

    const emails =
      data?.data?.map((user: any) => ({
        label: user.officialInfo?.email,
        value: user.officialInfo?.email,
      })) || [];

    setOptions(emails);
  };

  const handleComposeClose = () => {
    setShowComposeModal(false);
    searchParams.delete("mail_id");
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  useEffect(() => {
    if (emailError) {
      message.error(emailError);
    }
  }, [emailError]);

  useEffect(() => {
    if (mail_id) {
      setShowComposeModal(true);
      setSubject("");
      setEmailBody("");
      setRecipientEmail("");
      setCcvalues([]);
      setBccValues([]);
      setTimeout(() => {
        setActiveCc(false);
        setActiveBcc(false);
      }, 0);
    }
  }, [mail_id]);

  useEffect(() => {
    const ccEmails =
      singleEmailData?.data?.cc_users?.map(
        (user: any) => user.officialInfo?.email,
      ) || [];

    const bccEmails =
      singleEmailData?.data?.bcc_users?.map(
        (user: any) => user.officialInfo?.email,
      ) || [];

    if (singleEmailData?.data?.subject) {
      setSubject(singleEmailData.data.subject);
    }

    if (singleEmailData?.data?.body) {
      setEmailBody(singleEmailData.data.body);
    }

    if (singleEmailData?.data?.receiver?.officialInfo?.email) {
      setRecipientEmail(singleEmailData?.data?.receiver?.officialInfo?.email);
    }

    if (singleEmailData?.data?.cc_users?.length !== 0) {
      if (singleEmailData?.data?.cc_users?.length !== 0) {
        setActiveCc(true);
        setCcvalues(ccEmails);
      }
    }

    if (singleEmailData?.data?.bcc_users?.length !== 0) {
      if (singleEmailData?.data?.bcc_users?.length !== 0) {
        setActiveBcc(true);
        setBccValues(bccEmails);
      }
    }
  }, [mail_id, singleEmailData, auth]);
  // useEffect(() => {
  //   if (!singleEmailData?.data) return;

  //   const { subject, body, receiver, cc_users, bcc_users } =
  //     singleEmailData.data;

  //   if (subject) setSubject(subject);
  //   if (body) setEmailBody(body);

  //   if (receiver?.officialInfo?.email) {
  //     setRecipientEmail(receiver.officialInfo.email);
  //   }

  //   if (cc_users?.length) {
  //     setActiveCc(true);
  //     setCcvalues(cc_users.map((u: any) => u.officialInfo?.email));
  //   }

  //   if (bcc_users?.length) {
  //     setActiveBcc(true);
  //     setBccValues(bcc_users.map((u: any) => u.officialInfo?.email));
  //   }
  // }, [singleEmailData, mail_id, auth]);

  useEffect(() => {
    if (isSuccess) {
      setShowComposeModal(false);
      toast("success", "Email sent successfully");
      if (mail_id) {
        deleteMail({
          id: mail_id,
          data: {
            userId: auth.user?.id,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);
  useEffect(() => {
    if (isDeleteMailSuccess) {
      toast("success", "Draft email deleted successfully!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteMailSuccess]);
  useEffect(() => {
    if (isSaveDraftSuccess) {
      toast("success", "Mail drafted successfully!");
      handleClose();
      setShowComposeModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaveDraftSuccess]);

  useEffect(() => {
    if (isUpdateDraftSuccess) {
      // toast("success", "Mail drafted successfully!");
      handleClose();
      setShowComposeModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateDraftSuccess]);

  useEffect(() => {
    if (showComposeModal === false) {
      setSubject("");
      setEmailBody("");
      setRecipientEmail("");
      setActiveCc(false);
      setActiveBcc(false);
      setCcvalues([]);
      setBccValues([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showComposeModal]);

  let basePath = "/user/communication/email";
  if (auth?.role === "super_admin" || auth?.role === "Admin") {
    basePath = "/admin/communication/email";
  }

  const navlinks = [
    {
      id: 1,
      icon: <InboxIcon />,
      label: "Inbox",
      path: `${basePath}/inbox`,
    },
    {
      id: 2,
      icon: <StarredIcon />,
      label: "Starred",
      path: `${basePath}/starred`,
    },
    {
      id: 3,
      icon: <SentIcon2 />,
      label: "Sent",
      path: `${basePath}/sent`,
    },
    {
      id: 4,
      icon: <DraftIcon />,
      label: "Draft",
      path: `${basePath}/draft`,
    },
  ];
  // if (isLoading || isSingleEmailLoading) {
  //   return <Loader />;
  // }
  const handleBlur = () => {
    const isValidEmail = (email: string): boolean => mailRegex.test(email);
    if (recipientEmail && !isValidEmail(recipientEmail)) {
      setEmailError("Please enter a valid email address.");
      setRecipientEmail("");
    } else {
      setEmailError(null);
    }
  };

  return (
    <SidebarLayout title="Communication">
      <div className="grid grid-cols-4 gap-4 bg-white p-2">
        <div className="col-span-1">
          <div>
            <Button
              className="flex items-center gap-2 w-full justify-center"
              size="large"
              onClick={() => setShowComposeModal(true)}
            >
              <AddIcon />
              <span className="text-sm">Compose</span>
            </Button>
          </div>
          <div className="mt-4">
            {navlinks.map((item) => (
              <NavLink
                to={item.path}
                key={item.id}
                className={({ isActive }) =>
                  `${
                    isActive
                      ? "!bg-[#FFF1EB] text-[#FE7235]"
                      : "text-gray-700 hover:bg-gray-200"
                  } hover:!text-[#FE7235] hover:!bg-[#FFF1EB] text-base`
                }
              >
                <div className="flex gap-2 items-center p-3">
                  <div>{item.icon}</div>
                  <div className="">{item.label}</div>
                </div>
              </NavLink>
            ))}
          </div>
        </div>
        <div className="col-span-3">
          {/* <div>
            <Input
              size="large"
              placeholder="Search"
              prefix={<SearchIcon />}
              suffix={<FilterIcon />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div> */}
          {children}
        </div>
      </div>
      {showComposeModal && (
        <section className="fixed bottom-0 right-0 sm:right-10 w-full sm:w-[500px] max-h-[95vh] sm:max-h-[85vh] h-auto p-4 z-50 bg-white border border-slate-200 rounded-md overflow-hidden overflow-y-auto flex flex-col">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">New Message</span>
            <span
              className="cursor-pointer"
              onClick={() => {
                handleDraftMail();
                handleComposeClose();
              }}
            >
              <XIcon />
            </span>
          </div>
          <div className="border-b border-slate-200 mt-2 flex items-center">
            <div className="w-full">
              <AutoComplete
                value={recipientEmail}
                onSearch={handleSearch}
                options={options}
                placeholder="Recipients"
                allowClear={true}
                className="w-full"
                onChange={(value) => {
                  setRecipientEmail(value);
                  setEmailError(null); // clear error on change
                }}
                onBlur={handleBlur}
              >
                <Input className="!border-none !shadow-none focus:!shadow-none focus:!ring-0" />
              </AutoComplete>
              {/* {emailError&&<span className="text-red-500 text-sm">{emailError}</span>} */}
            </div>

            <div className="flex gap-4 ml-auto">
              {!activeCc && (
                <span
                  onClick={() => setActiveCc(true)}
                  className="cursor-pointer"
                >
                  Cc
                </span>
              )}
              {!activeBcc && (
                <span
                  onClick={() => setActiveBcc(true)}
                  className="cursor-pointer"
                >
                  Bcc
                </span>
              )}
            </div>
          </div>
          {activeCc && (
            <div className="border-b border-slate-200 mt-2 flex">
              <div className="w-full">
                <Select
                  mode="tags"
                  placeholder="Cc"
                  value={ccValues}
                  onChange={(value) => setCcvalues(value)}
                  onSearch={handleSearch}
                  options={options}
                  className="w-full !border-none !shadow-none focus:!shadow-none focus:!ring-0"
                  style={{
                    width: "100%",
                    border: "none",
                    boxShadow: "none",
                  }}
                  dropdownStyle={{
                    borderRadius: 0,
                  }}
                  bordered={false}
                />
              </div>
              <button
                className=""
                onClick={() => {
                  setActiveCc(false);
                  setCcvalues([]);
                }}
              >
                <CloseIcon />
              </button>
            </div>
          )}
          {activeBcc && (
            <div className="border-b border-slate-200 mt-2 flex">
              <div className="w-full">
                <Select
                  mode="tags"
                  placeholder="Bcc"
                  value={bccValues}
                  onChange={(value) => setBccValues(value)}
                  onSearch={handleSearch}
                  options={options}
                  className="w-full !border-none !shadow-none focus:!shadow-none focus:!ring-0"
                  style={{
                    width: "100%",
                    border: "none",
                    boxShadow: "none",
                  }}
                  dropdownStyle={{
                    borderRadius: 0,
                  }}
                  bordered={false}
                />
              </div>
              <button
                className=""
                onClick={() => {
                  setActiveBcc(false);
                  setBccValues([]);
                }}
              >
                <CloseIcon />
              </button>
            </div>
          )}
          <div className="border-b border-slate-200 mt-2 flex">
            <Input
              className="border-none focus:shadow-none flex-1"
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div className="border-b border-slate-200 mt-2 flex">
            <Input.TextArea
              className="border-none focus:shadow-none flex-1 resize-none"
              style={{ resize: "none" }}
              placeholder="Message will be in here"
              rows={(() => {
                let row = 13;
                if (activeCc) row -= 2;
                if (activeBcc) row -= 2;

                return row;
              })()}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
            />
          </div>
          <ul className="space-y-1">
            {attachments.map((url, index) => {
              const fileName = decodeURIComponent(url.split("/").pop() || "");
              return (
                <li
                  key={index}
                  className="max-w-sm truncate text-sm text-blue-700"
                  title={fileName}
                >
                  {fileName}
                </li>
              );
            })}
          </ul>

          <div className="flex justify-between pt-4">
            <div className="flex gap-4 items-center">
              <Button
                type="primary"
                className="w-[100px]"
                // trigger={["click"]}
                // icon={
                //   <div className="text-white relative top-1">
                //     <CaretDownIcon />
                //   </div>
                // }
                // menu={{
                //   items: [],
                // }}
                disabled={isLoading}
                onClick={handleSendMail}
              >
                Send
              </Button>

              {/* <div className="cursor-pointer">
                <AttachmentIcon />
              </div> */}
              <label htmlFor="attachment">
                <input
                  type="file"
                  id="attachment"
                  className="hidden"
                  onChange={handleImageChange}
                  // accept="image/*"
                  multiple // allow selecting multiple files
                />
                <AttachmentIcon />
              </label>
            </div>
            <Button
              onClick={() => {
                handleOpen({ type: "VIEW" });
              }}
            >
              <DeleteIcon />
            </Button>
          </div>
        </section>
      )}

      {open.type === "VIEW" && (
        <DeleteConfirmationModal
          open={open.open}
          handleClose={handleClose}
          handleDraftMail={handleDraftMail}
          handleDeleteMail={handleDeleteMail}
          setShowComposeModal={setShowComposeModal}
          loading={isSaveDradtLoading || isUpdateDraftLoading}
        />
      )}
    </SidebarLayout>
  );
}
